/**
 * Font Switcher Component
 *
 * Allows users to switch between different font families.
 */

import { Card, Space, Typography, Select, Tag } from 'antd';
import { FontSizeOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useTheme } from '@contexts/ThemeContext';

const { Title, Text, Paragraph } = Typography;

export default function FontSwitcher() {
  const { fontName, setFont, availableFonts, currentFont } = useTheme();

  // Group fonts by category
  const fontsByCategory = availableFonts.reduce(
    (acc, font) => {
      if (!acc[font.category]) {
        acc[font.category] = [];
      }
      acc[font.category].push(font);
      return acc;
    },
    {} as Record<string, typeof availableFonts>
  );

  const categoryLabels: Record<string, string> = {
    'sans-serif': 'Sans-Serif',
    serif: 'Serif',
    display: 'Display',
  };

  return (
    <Card
      title={
        <Space>
          <FontSizeOutlined />
          <span>Typography</span>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Choose your preferred font family. The selected font will be applied throughout the
        application.
      </Paragraph>

      {/* Current font preview */}
      <div
        style={{
          padding: 16,
          marginBottom: 16,
          borderRadius: 8,
          backgroundColor: 'var(--layout-content-bg)',
          border: '1px solid var(--border-color)',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          PREVIEW
        </Text>
        <div style={{ fontFamily: currentFont.fontFamily, marginTop: 8 }}>
          <Title level={4} style={{ margin: 0, fontFamily: 'inherit' }}>
            {currentFont.displayName}
          </Title>
          <Text style={{ fontFamily: 'inherit' }}>
            The quick brown fox jumps over the lazy dog. 0123456789
          </Text>
        </div>
      </div>

      {/* Font selector dropdown with categories */}
      <Select
        value={fontName}
        onChange={setFont}
        style={{ width: '100%' }}
        size="large"
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        optionLabelProp="label"
      >
        {Object.entries(fontsByCategory).map(([category, fonts]) => (
          <Select.OptGroup key={category} label={categoryLabels[category] || category}>
            {fonts.map((font) => (
              <Select.Option key={font.name} value={font.name} label={font.displayName}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: font.fontFamily, flex: 1 }}>{font.displayName}</span>
                  {font.name === fontName && (
                    <CheckCircleFilled style={{ color: 'var(--accent-warm)' }} />
                  )}
                </div>
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </Select>

      {/* Quick select popular fonts */}
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          POPULAR CHOICES
        </Text>
        <Space wrap>
          {['system', 'inter', 'roboto', 'poppins', 'plus-jakarta', 'dm-sans'].map((name) => {
            const font = availableFonts.find((f) => f.name === name);
            if (!font) return null;
            return (
              <Tag
                key={name}
                onClick={() => setFont(name)}
                style={{
                  cursor: 'pointer',
                  fontFamily: font.fontFamily,
                  padding: '4px 12px',
                  border: fontName === name ? '2px solid var(--accent-warm)' : undefined,
                }}
              >
                {font.displayName}
                {fontName === name && (
                  <CheckCircleFilled
                    style={{ marginLeft: 4, color: 'var(--accent-warm)', fontSize: 12 }}
                  />
                )}
              </Tag>
            );
          })}
        </Space>
      </div>
    </Card>
  );
}
